import React, { createContext, type ReactNode, useContext, useMemo } from 'react'
import { ChangeGroupImageOverlay } from '@fs/zion-group-management'
import { useOverlay, type UseOverlayReturnOptions } from '@fs/zion-ui'
import { type GroupSummary } from '@fs/zion-tree-types'

// TOdo remove JSDocs in new pr

type GroupManagementValue = {
  group: GroupSummary
  refetchGroup: () => void
  onLeaveGroup: () => void
  changeImageOverlay: UseOverlayReturnOptions
}

const GroupManagementGroupContext = createContext<GroupManagementValue | undefined>(undefined)

/**
 * @typedef {object} GroupManagementValue
 * @property {import('@fs/zion-tree-types').GroupSummary} group
 * @property {Function} refetchGroup
 * @property {Function} onLeaveGroup
 * @property {import('@fs/zion-ui').UseOverlayReturnOptions} changeImageOverlay
 */

/**
 *
 * @returns {GroupManagementValue}
 */

export const useGroupManagementGroupContext = (): GroupManagementValue | undefined =>
  useContext(GroupManagementGroupContext)

type GroupManagementGroupProviderProps = Omit<GroupManagementValue, 'changeImageOverlay'> & {
  children: ReactNode
}

/**
 *
 * @param {GroupManagementValue & {children: import('react').ReactNode}} props
 * @returns {JSX.Element}
 */

export default function GroupManagementGroupProvider({
  children,
  group,
  refetchGroup,
  onLeaveGroup,
}: GroupManagementGroupProviderProps): JSX.Element {
  const changeImageOverlay = useOverlay()
  const value = useMemo(
    () => ({
      group,
      refetchGroup,
      onLeaveGroup,
      changeImageOverlay,
    }),
    [group, refetchGroup, onLeaveGroup, changeImageOverlay]
  )
  return (
    <GroupManagementGroupContext.Provider value={value}>
      {children}
      <ChangeGroupImageOverlay group={group} overlay={changeImageOverlay} onGroupImageChange={refetchGroup} />
    </GroupManagementGroupContext.Provider>
  )
}
